import { useEffect, useState } from 'react';
import { fetchLibrary, type CatalogCourse, type CourseSummary, type LibraryView } from '../api';
import { useTenant } from '../app';
import { CheckIcon, Kicker, Link, LockIcon, PlayIcon, ProgressBar, Thumb, TwoLineTitle } from '../components';
import { formatClock, formatDurationBig, formatPrice, pad2, plural } from '../format';

/** Die Kursbibliothek (Konzept 4.2): Weiterlernen, Meine Kurse, Weitere Kurse. */
export function LibraryPage() {
  const tenant = useTenant();
  const [view, setView] = useState<LibraryView | null | undefined>(undefined);

  useEffect(() => {
    document.title = `Bibliothek – ${tenant.name}`;
    fetchLibrary()
      .then(setView)
      .catch(() => setView(null));
  }, [tenant.name]);

  if (view === undefined) return <main className="page" />;
  if (view === null) {
    return (
      <main className="page page--message">
        <h1>Die Bibliothek lässt sich gerade nicht laden</h1>
        <p className="muted">Bitte lade die Seite neu.</p>
      </main>
    );
  }

  const firstName = view.customer.name?.split(' ')[0];
  return (
    <main className="page library">
      <section className="library__head">
        <Kicker>Deine Bibliothek</Kicker>
        <h1 className="library__greeting">{firstName ? `Hallo ${firstName}.` : 'Hallo.'}</h1>
        <p className="muted library__lead">
          {view.mine.length === 0
            ? 'Du hast noch keinen Kurs freigeschaltet. Unten findest du, was es gibt.'
            : view.resume
              ? 'Mach einfach da weiter, wo du aufgehört hast – alles ist gespeichert, auch auf dem Handy.'
              : 'Alles erledigt. Unten findest du, was es noch gibt.'}
        </p>
      </section>

      {view.resume && <ResumeBanner resume={view.resume} />}

      {view.mine.length > 0 && (
        <section className="section">
          <div className="section__head">
            <Kicker>Meine Kurse</Kicker>
            <h2>{plural(view.mine.length, 'Kurs', 'Kurse')} freigeschaltet</h2>
          </div>
          <div className="grid grid--courses">
            {view.mine.map((c, i) => (
              <MineCard key={c.slug} course={c} index={i} />
            ))}
          </div>
        </section>
      )}

      {view.more.length > 0 && (
        <section className="section">
          <div className="section__head">
            <Kicker>Weitere Kurse</Kicker>
            <h2>Das gibt es noch</h2>
            <p className="muted">Ein Klick zeigt dir, was drin ist – inklusive kostenloser Vorschau-Lektionen.</p>
          </div>
          <div className="grid grid--courses">
            {view.more.map((c, i) => (
              <CatalogCard key={c.slug} course={c} index={i + 10} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function ResumeBanner({ resume }: { resume: NonNullable<LibraryView['resume']> }) {
  const href = `/kurs/${resume.course.slug}/lektion/${resume.lesson.number}`;
  return (
    <Link href={href} className="resume">
      <Thumb src={resume.course.imageUrl} seed={resume.lesson.number} className="resume__thumb">
        <span className="resume__play">
          <PlayIcon />
        </span>
      </Thumb>
      <div className="resume__body">
        <Kicker>Weiterlernen</Kicker>
        <h2 className="resume__title">{resume.course.title.replace('\n', ' ')}</h2>
        <p className="resume__lesson">
          <span className="resume__number">{pad2(resume.lesson.number)}</span>
          {resume.lesson.title}
          {resume.positionSeconds > 0 && <span className="muted"> · weiter ab {formatClock(resume.positionSeconds)}</span>}
        </p>
        <div className="resume__progress">
          <ProgressBar percent={resume.course.percent} thin />
          <span className="small muted">
            {resume.course.completedLessons} von {resume.course.lessonCount} Lektionen erledigt
          </span>
        </div>
      </div>
      <span className="resume__cta button">Weiter</span>
    </Link>
  );
}

function MineCard({ course, index }: { course: CourseSummary; index: number }) {
  const done = course.percent >= 100;
  const dur = formatDurationBig(course.durationSeconds);
  return (
    <Link href={`/kurs/${course.slug}`} className={`course-card${done ? ' course-card--done' : ''}`}>
      <Thumb src={course.imageUrl} seed={index + 1}>
        {course.kicker && <span className="thumb__kicker">{course.kicker}</span>}
        <TwoLineTitle title={course.title} as="h3" className="thumb__title" />
        {done && (
          <span className="thumb__badge">
            <CheckIcon /> Abgeschlossen
          </span>
        )}
      </Thumb>
      <div className="course-card__body">
        <p className="course-card__meta">
          {plural(course.lessonCount, 'Lektion', 'Lektionen')} · {dur.value} {dur.label}
        </p>
        {course.subtitle && <p className="muted small">{course.subtitle}</p>}
        <div className="course-card__progress">
          <ProgressBar percent={course.percent} thin />
          <span className="small">{course.percent} %</span>
        </div>
      </div>
    </Link>
  );
}

function CatalogCard({ course, index }: { course: CatalogCourse; index: number }) {
  const dur = formatDurationBig(course.durationSeconds);
  const free = course.product?.priceCents === 0;
  return (
    <Link href={`/kurs/${course.slug}`} className="course-card course-card--locked">
      <Thumb src={course.imageUrl} seed={index + 1}>
        {course.kicker && <span className="thumb__kicker">{course.kicker}</span>}
        <TwoLineTitle title={course.title} as="h3" className="thumb__title" />
        <span className={`thumb__badge${free ? ' thumb__badge--free' : ''}`}>
          {free ? 'Kostenlos' : <LockIcon />}
          {!free && course.product && <> {formatPrice(course.product.priceCents, course.product.currency)}</>}
        </span>
      </Thumb>
      <div className="course-card__body">
        <p className="course-card__meta">
          {plural(course.lessonCount, 'Lektion', 'Lektionen')} · {dur.value} {dur.label}
        </p>
        {course.subtitle && <p className="muted small">{course.subtitle}</p>}
        <span className="course-card__more">Ansehen →</span>
      </div>
    </Link>
  );
}
